import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useCases } from "@/lib/cases";
import { ArrowRight, Cake, Building2, Users, GraduationCap, ShieldCheck } from "lucide-react";
import { useT } from "@/lib/i18n";

const CDN = "https://www.bosquesuspendido.com/wp-content/uploads";

export const Route = createFileRoute("/services/casos")({
  component: Casos,
  head: () => ({
    meta: [
      { title: "Escolares y Grupos — Bosque Suspendido" },
      { name: "description", content: "Programas para colegios, despedidas, empresas, cumpleaños y grupos en el Bosque Suspendido. Más de 500 escolares simultáneamente." },
      { property: "og:title", content: "Escolares y Grupos — Bosque Suspendido" },
      { property: "og:description", content: "Programas educativos y celebraciones en el pinar de La Juliana." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/services/casos" },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/services/casos" }],
  }),
});

const AUDIENCES = [
  { id: "escolares", icon: GraduationCap, title: "Escolares", desc: "Programas para todos los ciclos y edades. Hasta 500 escolares simultáneamente, con aula de formación y monitores titulados." },
  { id: "cumples", icon: Cake, title: "Cumpleaños", desc: "Paquetes de Aventura, GelBall, Escalador y Arqueros, completables con menús del restaurante para un día inolvidable." },
  { id: "empresas", icon: Building2, title: "Empresas", desc: "Aula equipada, servicios de restauración y actividades de team building. El emplazamiento ideal para tu jornada." },
  { id: "grupos", icon: Users, title: "Grupos", desc: "Comuniones, despedidas, clubes deportivos, ayuntamientos, grupos de amigos… todos tienen cabida en el pinar." },
];

function Casos() {
  const { tk } = useT();
  const cases = useCases();

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-gold/15 bg-card/30">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("cases.kicker", "Escolares y grupos")}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl mt-5 max-w-4xl mx-auto">
            {tk("cases.title.a", "El plan perfecto ")}
            <em className="not-italic gold-gradient">{tk("cases.title.b", "para tu grupo")}</em>
          </h1>
          <p className="mt-6 text-foreground/80 max-w-2xl mx-auto leading-relaxed">
            {tk(
              "cases.intro",
              "Programas para colegios, empresas, despedidas, cumpleaños y clubes deportivos. Podemos atender más de 500 personas simultáneamente.",
            )}
          </p>
          <div className="mt-10 inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-foreground/70 border border-gold/40 bg-background/40 backdrop-blur-sm rounded-full px-5 py-2.5">
            <ShieldCheck size={14} className="text-gold" />
            {tk("cases.disclaimer", "Reserva previa obligatoria y gratuita.")}
          </div>
        </div>
      </section>

      {/* Audience tiles */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCES.map(({ id, icon: Icon, title, desc }) => (
              <div key={id} className="rounded-2xl border border-gold/20 bg-background/40 p-7 flex flex-col hover:border-gold/60 transition-colors">
                <Icon className="text-gold" size={26} strokeWidth={1.4} />
                <h3 className="mt-4 font-serif text-xl">{tk(`cases.audience.${id}.t`, title)}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{tk(`cases.audience.${id}.d`, desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thematic programs */}
      <section>
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("cases.programs.kicker", "Programas temáticos")}</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">{tk("cases.programs.title", "Aventuras con un guion")}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {cases.map((c) => (
              <article
                key={c.slug}
                id={c.slug}
                className="scroll-mt-32 rounded-2xl border border-gold/20 bg-card/30 overflow-hidden flex flex-col"
              >
                {c.imageUrl && (
                  <div className="aspect-[16/9] overflow-hidden bg-card">
                    <img src={c.imageUrl} alt={c.headline} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-gold/80">
                    <span>{c.area}</span>
                    <span className="text-muted-foreground/60">· {c.year}</span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-4 leading-snug">{c.headline}</h3>
                  <div className="gold-divider my-5 max-w-[80px]" />
                  <p className="text-foreground/80 leading-relaxed text-sm flex-1">{c.summary}</p>
                  <div className="mt-6 text-xs tracking-[0.25em] uppercase text-gold/80">{c.role}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Multiaventura */}
      <section className="border-t border-gold/15 bg-card/20">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("cases.multi.kicker", "Programas de multiaventura")}</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4">{tk("cases.multi.title", "Configura tu jornada")}</h2>
            <p className="mt-5 text-muted-foreground max-w-2xl mx-auto leading-relaxed text-sm">
              {tk("cases.multi.intro", "Disponemos de múltiples actividades para configurar un programa adecuado a la edad y necesidades de tus alumnos. Consúltanos para diseñarlo juntos.")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { t: "Aventura en los árboles", d: "Desde 6 años. Multitud de actividades para ajustarnos a la edad y número de alumnos." },
              { t: "Paintball", d: "Específico para menores. Junto con la galería de tiro, una de sus actividades favoritas." },
              { t: "Archery", d: "Lucha de arqueros + tirolinas + galería: una combinación tremenda." },
            ].map((p) => (
              <div key={p.t} className="rounded-2xl border border-gold/20 bg-background/40 p-7">
                <h3 className="font-serif text-xl">{p.t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ciclos formativos */}
      <section className="border-t border-gold/15">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("cases.ciclos.kicker", "Ciclos formativos y universidades")}</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4">{tk("cases.ciclos.title", "Formación, prácticas y eventos técnicos")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { t: "Jornadas lúdico/formativas", d: "Catálogo amplio aunando componente lúdico y formativo en la proporción que solicite el docente." },
              { t: "Formación en turismo activo", d: "Escalada, trabajos verticales, montaje de instalaciones, poda en altura, vía ferrata, senderismo, piragüismo y más." },
              { t: "Prácticas laborales", d: "Estancias de prácticas adaptadas al programa formativo del centro." },
              { t: "Acampada / vivac", d: "Permanece en las instalaciones en modo vivac para actividades nocturnas, cocina de campaña o dormir bajo los pinos." },
              { t: "Venta de material", d: "Almacén y tienda online de material para turismo activo, deportes alternativos y material educativo." },
              { t: "Construimos aventura", d: "Rocódromos, boulders, circuitos desmontables, estaciones de trepa, vías ferratas urbanas. Más de 30 años construyendo." },
            ].map((p) => (
              <div key={p.t} className="rounded-2xl border border-gold/20 bg-card/30 p-7">
                <h3 className="font-serif text-xl">{p.t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Birthdays gallery */}
      <section id="cumples" className="scroll-mt-32 border-t border-gold/15 bg-card/20">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("cases.cumples.kicker", "Cumpleaños")}</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4">{tk("cases.cumples.title", "El cumpleaños que no se olvida")}</h2>
              <div className="gold-divider my-6 max-w-[80px]" />
              <p className="text-foreground/80 leading-relaxed">
                {tk("cases.cumples.text", "El lugar ideal para tu celebración más especial. Elige el paquete que más te guste y complétalo con los servicios de nuestro restaurante. Todos los programas van monitorizados por nuestro personal y cuentan con seguro RC.")}
              </p>
              <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
                <li>• Paquete Aventura · GelBall · Escalador · Arqueros</li>
                <li>• Menús personalizables con el restaurante</li>
                <li>• Monitores titulados y seguro RC</li>
              </ul>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 wood-btn px-7 py-3 text-xs tracking-[0.3em] uppercase">
                {tk("cases.cumples.cta", "Reservar cumpleaños")} <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                `${CDN}/2024/12/BANERS-CUMPLES-AVENTURA-2.jpg`,
                `${CDN}/2024/12/BANERS-CUMPLES-GELBALL.jpg`,
                `${CDN}/2024/11/Escalador1-1.jpg`,
                `${CDN}/2024/11/Arqueros1.jpg`,
              ].map((src) => (
                <div key={src} className="rounded-xl overflow-hidden border border-gold/20 aspect-square bg-card">
                  <img src={src} alt="Cumpleaños Bosque Suspendido" loading="lazy" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
